export type StorageKind = 'local' | 'session';

export class StorageService {
  private readonly appPrefix: string;
  private readonly defaultScreen?: string;

  constructor(appPrefix = 'kvx', defaultScreen?: string) {
    this.appPrefix = (appPrefix ?? 'kvx').trim() || 'kvx';
    this.defaultScreen = defaultScreen?.trim() || undefined;
  }

  public save = <T>(
    target: StorageKind,
    key: string,
    value: T,
    codTela?: string,
  ): void => {
    const store = this.resolve(target);
    if (!store) return;
    const k = this.buildKey(key, codTela ?? this.defaultScreen);
    store.setItem(k, this.safeStringify(value));
  };

  public read = <T>(
    target: StorageKind,
    key: string,
    codTela?: string,
  ): T | null => {
    const store = this.resolve(target);
    if (!store) return null;
    const k = this.buildKey(key, codTela ?? this.defaultScreen);
    const raw = store.getItem(k);
    return raw == null ? null : this.safeParse<T>(raw);
  };

  public remove = (
    target: StorageKind,
    key: string,
    codTela?: string,
  ): void => {
    const store = this.resolve(target);
    if (!store) return;
    const k = this.buildKey(key, codTela ?? this.defaultScreen);
    store.removeItem(k);
  };

  private resolve = (target: StorageKind): Storage | null => {
    if (typeof window === 'undefined') return null;
    const store =
      target === 'local' ? window.localStorage : window.sessionStorage;
    try {
      const test = `${this.appPrefix}::__t__`;
      store.setItem(test, '1');
      store.removeItem(test);
      return store;
    } catch {
      return null;
    }
  };

  private buildKey = (key: string, codTela?: string): string => {
    const k = key.trim();
    if (!k) throw new Error('A "key" é obrigatória.');
    const screen = (codTela ?? 'global').trim();
    return `${this.appPrefix}:${screen}:${k}`;
  };

  private safeStringify = (value: unknown): string => {
    try {
      return JSON.stringify(value);
    } catch {
      return JSON.stringify(String(value));
    }
  };

  private safeParse = <T>(raw: string): T => {
    try {
      return JSON.parse(raw) as T;
    } catch {
      return raw as unknown as T;
    }
  };
}
